/**
 * Recall Management API Client
 *
 * Wraps the recall endpoints from the tms-api-recall service (port 5012).
 * All requests go through the gateway via the singleton API client which
 * automatically attaches the Authorization header from the auth store.
 */
import type { AxiosResponse } from 'axios';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  RecallHospitalListQuery,
  RecallHospitalListResponse,
  RecallDetail,
  AffectedPatientsResponse,
  UpdateHospitalRecallStatus,
  UpdateHospitalRecallStatusResult,
} from '@tms-phoenix/tms-types';

const API_PREFIX = API_ROUTES.RECALL;

function api() {
  return getApiClient();
}

// ─── Recalls ──────────────────────────────────────────────────────────────────

/**
 * GET /api/v1/recalls
 *
 * List recalls for a hospital with cursor-based pagination.
 * Supports optional status and recall_reference filters.
 */
export function listRecalls(
  params: RecallHospitalListQuery,
): Promise<AxiosResponse<RecallHospitalListResponse>> {
  return api().get<RecallHospitalListResponse>(`${API_PREFIX}/recalls`, {
    params,
    paramsSerializer: { indexes: null },
  });
}

/**
 * GET /api/v1/recalls/:id
 *
 * Retrieve full details of a single recall.
 */
export function getRecallById(
  recallId: string,
): Promise<AxiosResponse<RecallDetail>> {
  return api().get<RecallDetail>(`${API_PREFIX}/recalls/${recallId}`);
}

/**
 * GET /api/v1/recalls/:id/affectedPatients
 *
 * Get patients affected by a recall — blocked feed splits and feeds
 * delivered from recalled products in the last 30 days.
 */
export function getAffectedPatients(
  recallId: string,
): Promise<AxiosResponse<AffectedPatientsResponse>> {
  return api().get<AffectedPatientsResponse>(
    `${API_PREFIX}/recalls/${recallId}/affectedPatients`,
  );
}

/**
 * PUT /api/v1/recalls/:id/hospitalStatus
 *
 * Approve or reject a recall at the hospital level.
 * On APPROVED, triggers inventory quarantine, batch recall,
 * split blocking, and feed schedule deactivation.
 */
export function updateHospitalRecallStatus(
  recallId: string,
  body: UpdateHospitalRecallStatus,
): Promise<AxiosResponse<UpdateHospitalRecallStatusResult>> {
  return api().put<UpdateHospitalRecallStatusResult>(
    `${API_PREFIX}/recalls/${recallId}/hospitalStatus`,
    body,
  );
}
